import { Link } from "react-router-dom";
import { Info, ExternalLink, ArrowRight } from "lucide-react";
import Layout from "@/components/Layout";
import SEOHead from "@/components/SEOHead";
import { PARTNER_LINK } from "@/lib/constants";

const CANONICAL = "https://heilys.info/heilys-partnerlink";

const FAQ: { q: string; a: string }[] = [
  {
    q: "Was ist der heilys Partnerlink?",
    a: "Der Partnerlink führt direkt in den offiziellen heilys Shop. Für dich ändert sich am Preis nichts – wir erhalten bei einer Bestellung eine Provision, mit der unabhängige Tests, Videos und Erfahrungsberichte finanziert werden.",
  },
  {
    q: "Ist heilys.info der offizielle heilys Shop?",
    a: "Nein. heilys.info ist eine unabhängige Informationsseite ohne direkte Verbindung zum Unternehmen heilys. Bestellungen, Konditionen, Versand und Support laufen ausschließlich über den offiziellen heilys Shop.",
  },
  {
    q: "Welche heilys Produkte gibt es?",
    a: "Das Sortiment umfasst großflächige Rotlichtpanels, das kompakte Gen30+ Vision mit 670-nm-Vision-Modus, die AddRed Schreibtischlampe für Bildschirmarbeit, tragbare Wearables sowie Ambient-Licht für die Abendroutine.",
  },
  {
    q: "Worauf sollte ich vor dem Kauf achten?",
    a: "Entscheidend sind Einsatzbereich (punktuell oder großflächig), die abgedeckten Wellenlängen (630–660 nm Rotlicht, 810–850 nm Nahinfrarot), die Bauform und der verfügbare Platz. Die technischen Daten findest du jeweils direkt im offiziellen Shop.",
  },
];

const breadcrumbStructuredData = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: "https://heilys.info/" },
    { "@type": "ListItem", position: 2, name: "heilys Partnerlink & Produktinfos", item: CANONICAL },
  ],
};

const faqStructuredData = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQ.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

const HeilysPartnerlink = () => {
  const handleAffiliate = (source: string) => window.lh?.trackAffiliate({ source });

  return (
    <Layout>
      <SEOHead
        title="heilys Partnerlink & Produktinfos – unabhängige Einordnung"
        description="Wie du heilys Produkte über den offiziellen Shop bestellst: Partnerlink, Produktkategorien, Wellenlängen und was du vor dem Kauf wissen solltest."
        canonical={CANONICAL}
        ogImage="https://heilys.info/images/hero-main.webp"
        ogImageAlt="heilys Partnerlink und Produktinfos – unabhängige Einordnung"
        schemaJson={[breadcrumbStructuredData, faqStructuredData]}
      />

      <section className="pt-10 pb-12 md:pt-16 md:pb-20">
        <div className="container max-w-3xl">
          <nav aria-label="Breadcrumb" className="mb-4 text-xs text-muted-foreground">
            <Link to="/" className="hover:text-foreground">Start</Link>
            <span className="mx-2">/</span>
            <span className="text-foreground">Partnerlink &amp; Produktinfos</span>
          </nav>
          <h1 className="font-display text-2xl font-bold text-foreground sm:text-3xl md:text-4xl">
            heilys Partnerlink &amp; Produktinfos – unabhängige Einordnung
          </h1>
          <p className="mt-4 text-sm text-muted-foreground md:text-base">
            Auf dieser Seite findest du die wichtigsten Informationen rund um den Kauf von heilys Produkten:
            welche Gerätetypen es gibt, wofür sie sich eignen und wie der Weg in den offiziellen heilys Shop
            über unseren Partnerlink funktioniert.
          </p>

          <div className="mt-6 flex items-start gap-3 rounded-xl border border-primary/40 bg-primary/5 p-4 sm:p-5">
            <div className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/15 text-primary">
              <Info className="h-4 w-4" aria-hidden="true" />
            </div>
            <div className="text-sm text-muted-foreground">
              <p className="font-display text-sm font-semibold text-foreground">
                Update ab 01.08.2026
              </p>
              <p className="mt-1">
                heilys hat das Partnerprogramm umgestellt. Sämtliche Konditionen werden zentral über den
                offiziellen Shop geregelt und gelten dort für alle Kunden gleichermaßen.
              </p>
            </div>
          </div>

          <div className="mt-6 flex flex-col gap-3 sm:flex-row">
            <a
              href={PARTNER_LINK}
              target="_blank"
              rel="sponsored noopener noreferrer"
              onClick={() => handleAffiliate("partnerinfo_page_top")}
              className="inline-flex items-center justify-center gap-2 rounded-md bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-colors hover:brightness-110"
            >
              Zum offiziellen heilys Shop
              <ExternalLink className="h-4 w-4" />
            </a>
            <Link
              to="/"
              className="inline-flex items-center justify-center gap-2 rounded-md border border-border bg-card px-5 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-secondary"
            >
              Zur heilys Übersicht
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <section className="border-t border-border/50 bg-card/30 py-10 md:py-14">
        <div className="container max-w-3xl">
          <h2 className="font-display text-xl font-bold text-foreground sm:text-2xl md:text-3xl">
            Wie das heilys Partnerprogramm heute funktioniert
          </h2>
          <div className="mt-4 space-y-3 text-sm text-muted-foreground md:text-base">
            <p>
              heilys hat sein Partnerprogramm umgestellt: Konditionen werden nicht mehr individuell je
              Partner geregelt, sondern zentral über den offiziellen Shop. Für dich bedeutet das maximale
              Transparenz – es gilt für alle Kunden dasselbe, direkt im Checkout.
            </p>
            <p>
              Unsere Rolle bleibt dieselbe: unabhängige Einordnung der Produkte, ehrliche Erfahrungswerte
              und ein direkter Weg zum Hersteller. Was du auf heilys.info liest, ist redaktionell und nicht
              vom Hersteller vorgegeben.
            </p>
          </div>
        </div>
      </section>

      <section className="py-10 md:py-14">
        <div className="container max-w-3xl">
          <h2 className="font-display text-xl font-bold text-foreground sm:text-2xl md:text-3xl">
            So gehst du bei einem heilys Kauf vor – 3 Schritte
          </h2>
          <ol className="mt-6 space-y-4">
            {[
              { n: 1, t: "Bedarf klären", d: "Punktuelle Anwendung (Gesicht, Gelenke) oder Ganzkörper? Danach richtet sich, ob ein kompaktes Gerät, ein Panel oder ein Wearable passt." },
              { n: 2, t: "Partnerlink öffnen", d: "Über den Button gelangst du in den offiziellen heilys Shop – ohne Mehrkosten für dich, aber mit Unterstützung für unabhängige Inhalte." },
              { n: 3, t: "Im Checkout bestellen", d: "Die im Shop hinterlegten Konditionen gelten für alle Kunden und werden im Checkout automatisch berücksichtigt." },
            ].map((s) => (
              <li key={s.n} className="flex gap-4 rounded-lg border border-border bg-card p-4">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary font-display text-sm font-bold text-primary-foreground">
                  {s.n}
                </span>
                <div>
                  <h3 className="font-display text-base font-semibold text-foreground">{s.t}</h3>
                  <p className="mt-1 text-sm text-muted-foreground">{s.d}</p>
                </div>
              </li>
            ))}
          </ol>
          <div className="mt-8 text-center">
            <a
              href={PARTNER_LINK}
              target="_blank"
              rel="sponsored noopener noreferrer"
              onClick={() => handleAffiliate("partnerinfo_page_steps")}
              className="inline-flex items-center justify-center gap-2 rounded-md bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-colors hover:brightness-110"
            >
              Zum offiziellen heilys Shop
              <ExternalLink className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>

      <section className="border-t border-border/50 bg-card/30 py-10 md:py-14">
        <div className="container max-w-3xl">
          <h2 className="font-display text-xl font-bold text-foreground sm:text-2xl md:text-3xl">
            Produktkategorien im Überblick
          </h2>
          <ul className="mt-4 space-y-2 text-sm text-muted-foreground md:text-base">
            <li>• <strong className="text-foreground">Rotlichtpanels</strong> – großflächige Anwendung für Rücken, Beine oder Ganzkörper</li>
            <li>• <strong className="text-foreground">Gen30+ Vision</strong> – kompaktes Gerät mit sechs Wellenlängen und 670-nm-Vision-Modus</li>
            <li>• <strong className="text-foreground">AddRed</strong> – Schreibtischlampe mit Rotlichtanteil für Bildschirmarbeit</li>
            <li>• <strong className="text-foreground">Wearables</strong> – tragbare Geräte für punktuelle Anwendung unterwegs</li>
            <li>• <strong className="text-foreground">Ambient-Licht</strong> – warmes Rotlicht für Abendroutine und Wohnraum</li>
          </ul>
        </div>
      </section>

      <section className="py-10 md:py-14">
        <div className="container max-w-3xl">
          <h2 className="font-display text-xl font-bold text-foreground sm:text-2xl md:text-3xl">
            Häufige Fragen zu Partnerlink &amp; heilys Produkten
          </h2>
          <div className="mt-6 space-y-4">
            {FAQ.map((f) => (
              <details key={f.q} className="group rounded-lg border border-border bg-card p-4">
                <summary className="cursor-pointer font-display text-sm font-semibold text-foreground sm:text-base">
                  {f.q}
                </summary>
                <p className="mt-3 text-sm text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-border/50 bg-card/30 py-10 md:py-14">
        <div className="container max-w-3xl text-center">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Vertrauen &amp; Quellen</p>
          <p className="mt-3 text-sm text-muted-foreground md:text-base">
            heilys ist eine Marke der weCare+ GmbH aus Bielefeld und auf Trustpilot mit
            <strong className="text-foreground"> 4,3 / 5</strong> bewertet. heilys.info ist eine
            unabhängige Informationsseite und kein offizieller Shop – bei Käufen über unsere
            Partnerlinks erhalten wir eine Provision.
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm">
            <Link to="/" className="inline-flex items-center gap-1 text-foreground hover:text-primary">
              <ArrowRight className="h-3.5 w-3.5" /> heilys Übersicht &amp; Partnerlink
            </Link>
            <Link to="/gen30-vision" className="inline-flex items-center gap-1 text-foreground hover:text-primary">
              <ArrowRight className="h-3.5 w-3.5" /> heilys Gen30+ Vision Erfahrungen
            </Link>
            <Link to="/rotlicht-erkaeltung" className="inline-flex items-center gap-1 text-foreground hover:text-primary">
              <ArrowRight className="h-3.5 w-3.5" /> Rotlicht bei Erkältung anwenden
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default HeilysPartnerlink;
